import { DownOutlined } from '@ant-design/icons';
import { Dropdown, type MenuProps } from 'antd';
import {
  getLocaleFlagUrl,
  LOCALES,
  DEFAULT_LOCALE,
  resolveLocale,
  type LocaleCode,
} from 'i18n/locales';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { MenuSize } from 'store/app/enums';
import { setLocale } from 'store/app/actions';
import { getLocale } from 'store/app/selectors';
import type { Locale } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import styled from 'styled-components';
import { isMultipleLanguagesDisabled } from 'utils/feature-flag-utils';

export const SCOPE_LANGUAGE_SELECT_DROPDOWN_CLASS =
  'scope-language-select-dropdown';

const LanguageSelectorWrapper = styled.div<{ $variant: 'sidebar' | 'user-menu' }>`
  display: flex;
  justify-content: ${({ $variant }) =>
    $variant === 'user-menu' ? 'stretch' : 'center'};
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const LanguageTrigger = styled.button<{
  $variant: 'sidebar' | 'user-menu';
  $isWide?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $variant, $isWide }) =>
    $variant === 'user-menu' && !$isWide ? 'center' : 'flex-start'};
  gap: ${({ $variant, $isWide }) =>
    $variant === 'user-menu' && $isWide ? '12px' : '4px'};
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 20px;
  color: ${({ $variant }) =>
    $variant === 'user-menu'
      ? 'var(--color-text-base, #2e2e2e)'
      : 'var(--color-text-muted, #879494)'};
  font: inherit;
  font-size: 14px;
  width: ${({ $variant, $isWide }) =>
    $variant === 'user-menu' && $isWide ? '100%' : 'auto'};
  min-height: 20px;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

/** Keeps the flag on the same 20×20 grid as other user-menu icons. */
const UserMenuFlagSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  line-height: 0;

  img,
  .scope-language-select-option__flag--compact,
  .scope-language-select-option__flag-fallback--compact {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 2px;
    display: block;
  }
`;

const LanguageLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: start;
  white-space: nowrap;
  line-height: 20px;
  unicode-bidi: isolate;
`;

const LanguageCaret = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 20px;
  flex-shrink: 0;
  color: var(--color-text-muted, #879494);
  font-size: 10px;
  line-height: 1;
`;

const LocaleFlag: React.FC<{
  flagCode: string;
  alt: string;
  compact?: boolean;
}> = ({ flagCode, alt, compact = false }) => {
  const [errored, setErrored] = React.useState(false);

  if (errored) {
    return (
      <span
        className={`scope-language-select-option__flag-fallback${
          compact ? ' scope-language-select-option__flag-fallback--compact' : ''
        }`}
      >
        {flagCode}
      </span>
    );
  }

  return (
    <img
      className={`scope-language-select-option__flag${
        compact ? ' scope-language-select-option__flag--compact' : ''
      }`}
      src={getLocaleFlagUrl(flagCode)}
      alt={alt}
      onError={() => setErrored(true)}
    />
  );
};

const LocaleMenuItemRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const LocaleMenuCode = styled.span`
  min-width: 22px;
  font-weight: 600;
  color: var(--color-text-base, #2e2e2e);
  direction: ltr;
  unicode-bidi: isolate;
`;

const LocaleMenuName = styled.span`
  color: var(--color-text-base, #2e2e2e);
  flex: 1;
  text-align: start;
  unicode-bidi: isolate;
`;

const LocaleMenuItem: React.FC<{
  flagCode: string;
  shortCode: string;
  name: string;
  direction?: 'ltr' | 'rtl';
}> = ({ flagCode, shortCode, name, direction = 'ltr' }) => (
  <LocaleMenuItemRow className="scope-language-select-option">
    <LocaleFlag flagCode={flagCode} alt={name} />
    <LocaleMenuCode className="scope-language-select-option__code">
      {shortCode}
    </LocaleMenuCode>
    <LocaleMenuName
      className="scope-language-select-option__name"
      dir={direction}
    >
      {name}
    </LocaleMenuName>
  </LocaleMenuItemRow>
);

type LanguageSelectorProps = {
  menuSize: MenuSize;
  /** Where the control is rendered. Defaults to sidebar (legacy top placement). */
  variant?: 'sidebar' | 'user-menu';
  /** Called when the language dropdown opens/closes (keeps parent menus open). */
  onOpenChange?: (open: boolean) => void;
};

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  menuSize,
  variant = 'sidebar',
  onOpenChange,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const locale = resolveLocale(useSelector(getLocale) as Locale);
  const currentUser = useSelector(getAuthUser);
  const isUserMenu = variant === 'user-menu';
  const isWide = menuSize === MenuSize.WIDE;

  const selectedLocale =
    LOCALES.find(({ code }) => code === locale) ??
    LOCALES.find(({ code }) => code === DEFAULT_LOCALE)!;

  const handleChange = React.useCallback(
    (value: LocaleCode) => {
      dispatch(setLocale(resolveLocale(value), currentUser?.userUuid));
    },
    [currentUser?.userUuid, dispatch]
  );

  const menuItems = React.useMemo<MenuProps['items']>(
    () =>
      LOCALES.map(({ code, intlCode, label, flagCode, direction }) => {
        const shortCode = intlCode.split('-')[0].toUpperCase();
        return {
          key: code,
          label: (
            <LocaleMenuItem
              flagCode={flagCode}
              shortCode={shortCode}
              name={label}
              direction={direction}
            />
          ),
        };
      }),
    []
  );

  const handleMenuClick: MenuProps['onClick'] = React.useCallback(
    ({ key }: { key: string }) => {
      handleChange(key as LocaleCode);
    },
    [handleChange]
  );

  if (isMultipleLanguagesDisabled()) {
    return null;
  }

  return (
    <LanguageSelectorWrapper
      $variant={variant}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Dropdown
        menu={{
          items: menuItems,
          selectedKeys: [locale],
          onClick: handleMenuClick,
        }}
        // rightTop = toward main content in LTR. Ant Design mirrors this in RTL
        // (Arabic sidebar is on the right), so the list still opens into the page.
        placement={isUserMenu ? 'rightTop' : 'bottom'}
        trigger={['click']}
        onOpenChange={onOpenChange}
        getPopupContainer={() => document.body}
        overlayClassName={SCOPE_LANGUAGE_SELECT_DROPDOWN_CLASS}
        classNames={{ root: SCOPE_LANGUAGE_SELECT_DROPDOWN_CLASS }}
      >
        <LanguageTrigger
          type="button"
          $variant={variant}
          $isWide={isWide}
          aria-label={t('navigation.language')}
        >
          {isUserMenu ? (
            <UserMenuFlagSlot>
              <LocaleFlag
                flagCode={selectedLocale.flagCode}
                alt={selectedLocale.label}
                compact
              />
            </UserMenuFlagSlot>
          ) : (
            <LocaleFlag
              flagCode={selectedLocale.flagCode}
              alt={selectedLocale.label}
              compact
            />
          )}
          {isUserMenu && isWide && (
            <LanguageLabel dir={selectedLocale.direction}>
              {selectedLocale.label}
            </LanguageLabel>
          )}
          {(isUserMenu ? isWide : true) && (
            <LanguageCaret>
              <DownOutlined />
            </LanguageCaret>
          )}
        </LanguageTrigger>
      </Dropdown>
    </LanguageSelectorWrapper>
  );
};
